import {
    useEffect,
    useState,
} from "react";

import {
    Box,
    Button,
    CardActions,
    CardContent,
    Grid,
    Paper,
    Skeleton,
    Typography,
} from "@mui/material";

import {
    alpha,
} from "@mui/material/styles";

import RefreshIcon from
    "@mui/icons-material/Refresh";

import {
    handleResponse,
} from "../util/integration";

import {
    fetchLeadAPI,
} from "../api/lead_api";

import {
    PRIMARY_COLOR,
    SECONDARY_COLOR,
} from "../util/constants";

import StaticMap from "./StaticMap";

const LEAD_STATUS = Object.freeze({
    created: "CREATED",
    forSale: "FOR_SALE",
    notPaidSaleReversed: "NOT_PAID_SALE_REVERSED",
    soldPaid: "SOLD_PAID",
    soldNotPaid: "SOLD_NOT_PAID",
    notSold: "NOT_SOLD",
    complained: "COMPLAINED",
    invalidated: "INVALIDATED",
});

const SALE_MODE = Object.freeze({
    auction: "AUCTION",
    offer: "OFFER",
});

const LABEL = Object.freeze({
    id: "ID",
    firstName: "Vorname",
    lastName: "Nachname",
    businessName: "Firma",
    retry: "Erneut versuchen",

    statusCreated: "Erstellt",
    statusForSale: "Im Verkauf",
    statusSoldNotPaid: "Verkauft – Zahlung offen",
    statusPurchasedNotPaid: "Gekauft – Zahlung offen",
    statusSoldPaid: "Verkauft – bezahlt",
    statusPurchasedPaid: "Gekauft – bezahlt",
    statusNotPaidSaleReversed: "Nicht bezahlt – Verkauf rückabgewickelt",
    statusNotSold: "Nicht verkauft",
    statusComplained: "Beanstandet",
    statusInvalidated: "Ungültig",

    modeAuction: "Auktion",
    modeOffer: "Sofortkauf",
});

const DEFAULT_VALUE = Object.freeze({
    empty: "–",
    dateLabel: "Erstellt am",
    actionLabel: "Lead öffnen",

    errorMessage:
        "Der Lead konnte nicht geladen werden.",
});

const MAP_CONFIG = Object.freeze({
    defaultLatitude: 52.52,
    defaultLongitude: 13.405,
    zoom: 5,
    height: 150,
});

// Default: Lead alle 30 Sekunden neu laden.
const POLLING_CONFIG = Object.freeze({
    intervalMs: 30000,
});

const STATUS_COLOR = Object.freeze({
    notSoldText:
        "rgb(183, 28, 28)",

    notSoldBackground:
        "rgb(255, 235, 238)",

    notSoldBorder:
        "rgb(239, 154, 154)",

    complainedText:
        "rgb(230, 81, 0)",

    complainedBackground:
        "rgb(255, 243, 224)",

    complainedBorder:
        "rgb(255, 183, 77)",

    invalidatedText:
        "rgb(66, 66, 66)",

    invalidatedBackground:
        "rgb(238, 238, 238)",

    invalidatedBorder:
        "rgb(189, 189, 189)",

    neutralText: "white",

    neutralBackground:
        "rgba(255, 255, 255, 0.18)",

    neutralBorder:
        "rgba(255, 255, 255, 0.38)",
});

const SALE_MODE_COLOR = Object.freeze({
    auctionText:
        "rgb(21, 101, 192)",

    auctionBackground:
        "rgb(227, 242, 253)",

    auctionBorder:
        "rgb(144, 202, 249)",

    offerText:
        "rgb(46, 125, 50)",

    offerBackground:
        "rgb(232, 245, 233)",

    offerBorder:
        "rgb(165, 214, 167)",
});

const SKELETON_ITEMS = Object.freeze([
    1,
    2,
    3,
    4,
    5,
    6,
]);

const STYLE = Object.freeze({
    dataValue: {
        wordBreak: "break-word",
    },

    surface: {
        display: "block",
        height: "100%",
        overflow: "hidden",
        borderRadius: 3,
    },

    loadingCard: {
        minHeight: 320,
    },

    loadingDivider: {
        mt: 2,
    },

    loadingGrid: {
        mt: 1,
    },

    errorCard: {
        minHeight: 220,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
    },

    errorMessage: {
        mt: 1,
    },

    card: {
        minHeight: 320,
        display: "flex",
        flexDirection: "column",
    },

    categoryTitle: {
        lineHeight: 1.25,
        minWidth: 0,
        wordBreak: "break-word",
    },

    badgeContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 1,
        flexWrap: "wrap",
        flexShrink: 0,
    },

    content: {
        flexGrow: 1,
        padding: "25px",
    },

    actions: {
        p: 2,
        pt: 0,
        padding: "25px",
    },
});

const createSurfaceStyles = (
    primaryColor,
) => {
    const overviewSurface = {
        ...STYLE.surface,
        borderColor:
            alpha(
                primaryColor,
                0.14,
            ),
        boxShadow:
            `0 12px 30px ${alpha(
                primaryColor,
                0.06,
            )}`,
    };

    return Object.freeze({
        loadingCard: {
            ...overviewSurface,
            ...STYLE.loadingCard,
        },

        errorCard: {
            ...overviewSurface,
            ...STYLE.errorCard,
        },

        card: {
            ...overviewSurface,
            ...STYLE.card,
        },
    });
};

const createColorStyles = (
    primaryColor,
    statusConfig,
    saleModeConfig,
) => Object.freeze({
    categoryHeader: {
        backgroundColor: primaryColor,
        padding: "20px",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
    },

    statusBadge: {
        flexShrink: 0,
        padding: "5px 10px",
        borderRadius: "999px",

        border: `1px solid ${
            statusConfig?.borderColor
            ?? STATUS_COLOR.neutralBorder
        }`,

        backgroundColor:
            statusConfig?.backgroundColor
            ?? STATUS_COLOR.neutralBackground,

        color:
            statusConfig?.color
            ?? STATUS_COLOR.neutralText,

        fontSize: "0.75rem",
        lineHeight: 1.4,
        whiteSpace: "nowrap",
    },

    saleModeBadge: {
        flexShrink: 0,
        padding: "5px 10px",
        borderRadius: "999px",

        border: `1px solid ${
            saleModeConfig?.borderColor
            ?? STATUS_COLOR.neutralBorder
        }`,

        backgroundColor:
            saleModeConfig?.backgroundColor
            ?? STATUS_COLOR.neutralBackground,

        color:
            saleModeConfig?.color
            ?? STATUS_COLOR.neutralText,

        fontSize: "0.75rem",
        lineHeight: 1.4,
        whiteSpace: "nowrap",
    },

    actionButton: {
        backgroundColor: primaryColor,
        color: "white",

        "&:hover": {
            backgroundColor: primaryColor,
            filter: "brightness(0.9)",
        },
    },
});

const defaultDateResolver = (lead) => {
    return (
        lead.creationTimestamp
        ?? lead.createdTimestamp
        ?? lead.createdAt
    );
};

const getCategoryName = (lead) => {
    return (
        lead.category?.name
        ?? lead.categoryName
    );
};

const defaultCurrentUserIdResolver = (
    contextAPI,
) => {
    return (
        contextAPI?.currentUser?.id
        ?? contextAPI?.user?.id
        ?? contextAPI?.currentUserId
        ?? contextAPI?.userId
        ?? null
    );
};

const defaultSellerIdResolver = (lead) => {
    return (
        lead?.authorId
        ?? lead?.seller?.id
        ?? lead?.sellerId
        ?? lead?.vendor?.id
        ?? lead?.vendorId
        ?? lead?.owner?.id
        ?? lead?.ownerId
        ?? null
    );
};

const getLatestApprovedPriceAgreement = (lead) => {
    const agreements = Array.isArray(
        lead?.priceAgreementList,
    )
        ? lead.priceAgreementList
        : [];

    return agreements
        .filter((agreement) => {
            return (
                agreement?.approvalTimestamp
                !== null
                && agreement?.approvalTimestamp
                !== undefined
            );
        })
        .reduce((latest, agreement) => {
            if (latest === null) {
                return agreement;
            }

            const currentOfferTimestamp = Number(
                agreement?.offerTimestamp ?? 0,
            );
            const latestOfferTimestamp = Number(
                latest?.offerTimestamp ?? 0,
            );

            if (
                currentOfferTimestamp
                !== latestOfferTimestamp
            ) {
                return currentOfferTimestamp
                    > latestOfferTimestamp
                    ? agreement
                    : latest;
            }

            return Number(
                agreement?.approvalTimestamp ?? 0,
            ) > Number(
                latest?.approvalTimestamp ?? 0,
            )
                ? agreement
                : latest;
        }, null);
};

const defaultBuyerIdResolver = (lead) => {
    const latestApprovedAgreement =
        getLatestApprovedPriceAgreement(lead);

    return (
        lead?.buyer?.id
        ?? lead?.buyerId
        ?? lead?.purchaser?.id
        ?? lead?.purchaserId
        ?? lead?.customer?.id
        ?? lead?.customerId
        ?? latestApprovedAgreement?.buyerId
        ?? latestApprovedAgreement?.buyer?.id
        ?? null
    );
};

const identifiersMatch = (
    firstIdentifier,
    secondIdentifier,
) => {
    if (
        firstIdentifier === null
        || firstIdentifier === undefined
        || secondIdentifier === null
        || secondIdentifier === undefined
    ) {
        return false;
    }

    return (
        String(firstIdentifier)
        === String(secondIdentifier)
    );
};

const getRoleAwareSaleStatusLabel = ({
    status,
    lead,
    currentUserId,
    getSellerId,
    getBuyerId,
}) => {
    const isBuyer = identifiersMatch(
        currentUserId,
        getBuyerId(lead),
    );

    const isSeller = identifiersMatch(
        currentUserId,
        getSellerId(lead),
    );

    switch (status) {
        case LEAD_STATUS.soldNotPaid:
            if (isBuyer && !isSeller) {
                return LABEL.statusPurchasedNotPaid;
            }

            return LABEL.statusSoldNotPaid;

        case LEAD_STATUS.soldPaid:
            if (isBuyer && !isSeller) {
                return LABEL.statusPurchasedPaid;
            }

            return LABEL.statusSoldPaid;

        default:
            return null;
    }
};

const getStatusConfig = (
    status,
    secondaryColor,
    roleAwareSaleStatusLabel = null,
) => {
    switch (status) {
        case LEAD_STATUS.created:
            return {
                label:
                    LABEL.statusCreated,

                color:
                    STATUS_COLOR.neutralText,

                backgroundColor:
                    STATUS_COLOR.neutralBackground,

                borderColor:
                    STATUS_COLOR.neutralBorder,
            };

        case LEAD_STATUS.forSale:
            return {
                label:
                    LABEL.statusForSale,

                color: "white",

                backgroundColor:
                    secondaryColor,

                borderColor:
                    secondaryColor,
            };

        case LEAD_STATUS.soldNotPaid:
            return {
                label:
                    roleAwareSaleStatusLabel
                    ?? LABEL.statusSoldNotPaid,

                color:
                    STATUS_COLOR.neutralText,

                backgroundColor:
                    STATUS_COLOR.neutralBackground,

                borderColor:
                    STATUS_COLOR.neutralBorder,
            };

        case LEAD_STATUS.soldPaid:
            return {
                label:
                    roleAwareSaleStatusLabel
                    ?? LABEL.statusSoldPaid,

                color:
                    STATUS_COLOR.neutralText,

                backgroundColor:
                    STATUS_COLOR.neutralBackground,

                borderColor:
                    STATUS_COLOR.neutralBorder,
            };

        case LEAD_STATUS.notPaidSaleReversed:
            return {
                label:
                    LABEL.statusNotPaidSaleReversed,

                color:
                    STATUS_COLOR.notSoldText,

                backgroundColor:
                    STATUS_COLOR.notSoldBackground,

                borderColor:
                    STATUS_COLOR.notSoldBorder,
            };

        case LEAD_STATUS.notSold:
            return {
                label:
                    LABEL.statusNotSold,

                color:
                    STATUS_COLOR.notSoldText,

                backgroundColor:
                    STATUS_COLOR.notSoldBackground,

                borderColor:
                    STATUS_COLOR.notSoldBorder,
            };

        case LEAD_STATUS.complained:
            return {
                label:
                    LABEL.statusComplained,

                color:
                    STATUS_COLOR.complainedText,

                backgroundColor:
                    STATUS_COLOR.complainedBackground,

                borderColor:
                    STATUS_COLOR.complainedBorder,
            };

        case LEAD_STATUS.invalidated:
            return {
                label:
                    LABEL.statusInvalidated,

                color:
                    STATUS_COLOR.invalidatedText,

                backgroundColor:
                    STATUS_COLOR.invalidatedBackground,

                borderColor:
                    STATUS_COLOR.invalidatedBorder,
            };

        default:
            return null;
    }
};

const getSaleMode = (lead) => {
    if (
        lead.status
        !== LEAD_STATUS.forSale
    ) {
        return null;
    }

    if (
        lead.recentAuction !== null
        && lead.recentAuction !== undefined
    ) {
        return SALE_MODE.auction;
    }

    if (
        lead.recentOffer !== null
        && lead.recentOffer !== undefined
    ) {
        return SALE_MODE.offer;
    }

    return null;
};

const getSaleModeConfig = (
    saleMode,
) => {
    switch (saleMode) {
        case SALE_MODE.auction:
            return {
                label:
                    LABEL.modeAuction,

                color:
                    SALE_MODE_COLOR.auctionText,

                backgroundColor:
                    SALE_MODE_COLOR
                        .auctionBackground,

                borderColor:
                    SALE_MODE_COLOR
                        .auctionBorder,
            };

        case SALE_MODE.offer:
            return {
                label:
                    LABEL.modeOffer,

                color:
                    SALE_MODE_COLOR.offerText,

                backgroundColor:
                    SALE_MODE_COLOR
                        .offerBackground,

                borderColor:
                    SALE_MODE_COLOR.offerBorder,
            };

        default:
            return null;
    }
};

const displayValue = (value) => {
    if (
        value === null
        || value === undefined
        || value === ""
    ) {
        return DEFAULT_VALUE.empty;
    }

    return value;
};

const buildCategoryTitle = (
    categoryName,
    zipCode,
) => {
    const titleParts = [
        categoryName,
        zipCode,
    ].filter((value) => {
        return (
            value !== null
            && value !== undefined
            && value !== ""
        );
    });

    if (titleParts.length === 0) {
        return DEFAULT_VALUE.empty;
    }

    return titleParts.join(" · ");
};

const formatDate = (value) => {
    if (
        value === null
        || value === undefined
        || value === ""
    ) {
        return DEFAULT_VALUE.empty;
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    return new Intl.DateTimeFormat(
        "de-DE",
        {
            dateStyle: "medium",
            timeStyle: "short",
        },
    ).format(date);
};

const normalizeCoordinate = (
    value,
    minimum,
    maximum,
) => {
    if (
        value === null
        || value === undefined
        || value === ""
    ) {
        return null;
    }

    const numericValue =
        Number(value);

    if (
        !Number.isFinite(numericValue)
        || numericValue < minimum
        || numericValue > maximum
    ) {
        return null;
    }

    return numericValue;
};

const getMapCoordinates = (lead) => {
    const latitude =
        normalizeCoordinate(
            lead?.latitude,
            -90,
            90,
        );

    const longitude =
        normalizeCoordinate(
            lead?.longitude,
            -180,
            180,
        );

    if (
        latitude === null
        || longitude === null
    ) {
        return {
            latitude:
                MAP_CONFIG.defaultLatitude,

            longitude:
                MAP_CONFIG.defaultLongitude,
        };
    }

    return {
        latitude,
        longitude,
    };
};

function DataField({
    label,
    value,
    fullWidth = false,
}) {
    return (
        <Grid
            item
            xs={12}
            sm={fullWidth ? 12 : 6}
        >
            <Typography
                variant="caption"
                color="text.secondary"
                component="div"
            >
                {label}
            </Typography>

            <Typography
                variant="body2"
                component="div"
                sx={STYLE.dataValue}
            >
                {displayValue(value)}
            </Typography>
        </Grid>
    );
}

function LoadingCard({
    surfaceStyle,
}) {
    return (
        <Paper
            variant="outlined"
            sx={surfaceStyle}
        >
            <CardContent>
                <Skeleton
                    variant="text"
                    width="50%"
                    height={38}
                />

                <Skeleton
                    variant="text"
                    width="30%"
                />

                <Skeleton
                    sx={STYLE.loadingDivider}
                    variant="rectangular"
                    height={1}
                />

                <Grid
                    container
                    spacing={2}
                    sx={STYLE.loadingGrid}
                >
                    {SKELETON_ITEMS.map(
                        (item) => (
                            <Grid
                                item
                                xs={6}
                                key={item}
                            >
                                <Skeleton
                                    variant="text"
                                    width="60%"
                                />

                                <Skeleton
                                    variant="text"
                                    width="90%"
                                />
                            </Grid>
                        ),
                    )}
                </Grid>
            </CardContent>
        </Paper>
    );
}

export default function LeadPreview({
    leadId,
    appAPI,
    contextAPI,

    dateLabel =
        DEFAULT_VALUE.dateLabel,

    getDateValue =
        defaultDateResolver,

    actionLabel =
        DEFAULT_VALUE.actionLabel,

    onAction,

    errorMessage =
        DEFAULT_VALUE.errorMessage,

    pollIntervalMs =
        POLLING_CONFIG.intervalMs,

    currentUserId,

    getCurrentUserId =
        defaultCurrentUserIdResolver,

    getSellerId =
        defaultSellerIdResolver,

    getBuyerId =
        defaultBuyerIdResolver,
}) {
    const [
        lead,
        setLead,
    ] = useState(null);

    const [
        isLoading,
        setIsLoading,
    ] = useState(true);

    const [
        hasError,
        setHasError,
    ] = useState(false);

    const [
        refreshVersion,
        setRefreshVersion,
    ] = useState(0);

    const accessToken =
        contextAPI.password;

    const resolvedCurrentUserId =
        currentUserId
        ?? getCurrentUserId(
            contextAPI,
        );

    const showMessage =
        appAPI.showMessage;

    const surfaceStyle =
        createSurfaceStyles(
            PRIMARY_COLOR,
        );

    useEffect(() => {
        let isActive = true;
        let requestIsPending = false;
        let hasLoadedSuccessfully = false;
        let hasReportedInitialError = false;

        const loadLead = (
            showLoadingState = false,
        ) => {
            if (
                !isActive
                || requestIsPending
            ) {
                return;
            }

            requestIsPending = true;

            if (showLoadingState) {
                setLead(null);
                setHasError(false);
                setIsLoading(true);
            }

            handleResponse(
                fetchLeadAPI( 
                    accessToken,
                    leadId,
                    contextAPI.id
                ),
                (response) => {
                    requestIsPending = false;

                    if (!isActive) {
                        return;
                    }

                    hasLoadedSuccessfully = true;
                    hasReportedInitialError = false;

                    setLead(response);
                    setHasError(false);
                    setIsLoading(false);
                },
                (message) => {
                    requestIsPending = false;

                    if (!isActive) {
                        return;
                    }

                    setIsLoading(false);

                    if (
                        !hasLoadedSuccessfully
                    ) {
                        setHasError(true);

                        if (
                            !hasReportedInitialError
                        ) {
                            hasReportedInitialError =
                                true;

                            showMessage(message);
                        }
                    }
                },
            );
        };

        loadLead(true);

        const normalizedPollInterval =
            Number(pollIntervalMs);

        const pollingIsEnabled =
            Number.isFinite(
                normalizedPollInterval,
            )
            && normalizedPollInterval > 0;

        // Polling starten; laufende Requests werden in loadLead übersprungen.
        const intervalId =
            pollingIsEnabled
                ? window.setInterval(
                    () => {
                        loadLead();
                    },
                    normalizedPollInterval,
                )
                : null;

        return () => {
            // Polling beim Unmount oder bei geänderten Abhängigkeiten beenden.
            isActive = false;

            if (intervalId !== null) {
                window.clearInterval(
                    intervalId,
                );
            }
        };
    }, [
        accessToken,
        leadId,
        pollIntervalMs,
        refreshVersion,
        showMessage,
    ]);

    if (isLoading) {
        return (
            <LoadingCard
                surfaceStyle={
                    surfaceStyle
                        .loadingCard
                }
            />
        );
    }

    if (
        hasError
        || lead === null
    ) {
        return (
            <Paper
                variant="outlined"
                sx={
                    surfaceStyle.errorCard
                }
            >
                <CardContent>
                    <Typography variant="h6">
                        Lead #{leadId}
                    </Typography>

                    <Typography
                        color="error"
                        variant="body2"
                        sx={STYLE.errorMessage}
                    >
                        {errorMessage}
                    </Typography>
                </CardContent>

                <CardActions>
                    <Button
                        startIcon={
                            <RefreshIcon />
                        }
                        onClick={() => {
                            setRefreshVersion(
                                (
                                    currentVersion,
                                ) => {
                                    return (
                                        currentVersion
                                        + 1
                                    );
                                },
                            );
                        }}
                    >
                        {LABEL.retry}
                    </Button>
                </CardActions>
            </Paper>
        );
    }

    const dateValue =
        getDateValue(lead);

    const categoryName =
        getCategoryName(lead);

    const categoryTitle =
        buildCategoryTitle(
            categoryName,
            lead.zipCode,
        );

    const roleAwareSaleStatusLabel =
        getRoleAwareSaleStatusLabel({
            status: lead.status,
            lead,
            currentUserId:
                resolvedCurrentUserId,
            getSellerId,
            getBuyerId,
        });

    const statusConfig =
        getStatusConfig(
            lead.status,
            SECONDARY_COLOR,
            roleAwareSaleStatusLabel,
        );

    const saleMode =
        getSaleMode(lead);

    const saleModeConfig =
        getSaleModeConfig(
            saleMode,
        );

    const colorStyle =
        createColorStyles(
            PRIMARY_COLOR,
            statusConfig,
            saleModeConfig,
        );

    const mapCoordinates =
        getMapCoordinates(lead);

    return (
        <Paper
            variant="outlined"
            sx={surfaceStyle.card}
        >
            <StaticMap
                latitude={
                    mapCoordinates.latitude
                }
                longitude={
                    mapCoordinates.longitude
                }
                zoom={MAP_CONFIG.zoom}
                height={MAP_CONFIG.height}
            />

            <Box
                sx={
                    colorStyle.categoryHeader
                }
            >
                <Typography
                    component="h2"
                    sx={STYLE.categoryTitle}
                >
                    {categoryTitle}
                </Typography>

                <Box
                    sx={
                        STYLE.badgeContainer
                    }
                >
                    {statusConfig !== null && (
                        <Box
                            component="span"
                            sx={
                                colorStyle
                                    .statusBadge
                            }
                        >
                            {statusConfig.label}
                        </Box>
                    )}

                    {saleModeConfig
                        !== null
                        && (
                            <Box
                                component="span"
                                sx={
                                    colorStyle
                                        .saleModeBadge
                                }
                            >
                                {
                                    saleModeConfig
                                        .label
                                }
                            </Box>
                        )}
                </Box>
            </Box>

            <CardContent
                sx={STYLE.content}
            >
                <Grid
                    container
                    spacing={2}
                >
                    <DataField
                        label={LABEL.id}
                        value={lead.id}
                    />

                    <DataField
                        label={dateLabel}
                        value={
                            formatDate(
                                dateValue,
                            )
                        }
                    />

                    <DataField
                        label={
                            LABEL.firstName
                        }
                        value={
                            lead.firstName
                        }
                    />

                    <DataField
                        label={
                            LABEL.lastName
                        }
                        value={
                            lead.lastName
                        }
                    />

                    <DataField
                        label={
                            LABEL.businessName
                        }
                        value={
                            lead.businessName
                        }
                        fullWidth
                    />
                </Grid>
            </CardContent>

            {onAction && (
                <CardActions
                    sx={STYLE.actions}
                >
                    <Button
                        variant="outlined"
                        onClick={onAction}
                        sx={
                            colorStyle
                                .actionButton
                        }
                    >
                        {actionLabel}
                    </Button>
                </CardActions>
            )}
        </Paper>
    );
} 